"use client";

import Image from "next/image";
import Link from "next/link";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

const delayClassMap = {
  0: "delay-0",
  10: "delay-100",
  15: "delay-150",
  20: "delay-200",
  30: "delay-300",
} as const;

function getDelayClass(delay: number) {
  return delayClassMap[Math.round(delay * 100) as keyof typeof delayClassMap] ?? "delay-0";
}

function useVisible(threshold = 0.1) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return [ref, visible] as const;
}

function Reveal({
  children,
  delay = 0,
  from = "bottom",
}: {
  children: ReactNode;
  delay?: number;
  from?: "bottom" | "left" | "right";
}) {
  const [ref, visible] = useVisible();

  const fromClasses = {
    bottom: "translate-y-8",
    left: "-translate-x-8",
    right: "translate-x-8",
  } as const;

  return (
    <div
      ref={ref}
      className={cn(
        "opacity-0",
        fromClasses[from],
        "transition-all duration-[650ms] ease-out",
        getDelayClass(delay),
        visible && "opacity-100 translate-x-0 translate-y-0"
      )}
    >
      {children}
    </div>
  );
}

function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "text-[11px] uppercase tracking-[0.15em] font-semibold text-[#1565C0] mb-3.5 block",
        className
      )}
    >
      {children}
    </span>
  );
}

function Divider({ className }: { className?: string }) {
  return <div className={cn("w-11 h-[3px] bg-[#1565C0] mb-6", className)} />;
}

function CheckIcon() {
  return (
    <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#1565C0] text-white">
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1.5 5.5L3.8 7.8L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

function RiskRow({ r, i }: { r: { risk: string; solution: string }; i: number }) {
  const [ref, visible] = useVisible();
  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300"] as const;

  return (
    <div
      ref={ref}
      className={cn(
        "grid gap-4 py-4 border-b border-[#E2E8F0] items-start md:grid-cols-[1fr_1.3fr]",
        delayClasses[i % delayClasses.length],
        visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
      )}
    >
      <div className="flex items-start gap-2">
        <span className="mt-[6px] block h-1.5 w-1.5 rounded-full bg-[#E65100]" />
        <span className="text-[12.5px] font-semibold text-[#0F172A] leading-6">{r.risk}</span>
      </div>
      <div className="flex items-start gap-2">
        <span className="mt-[6px] block h-1.5 w-1.5 rounded-full bg-[#1565C0]" />
        <span className="text-[12.5px] text-[#64748B] leading-[1.55]">{r.solution}</span>
      </div>
    </div>
  );
}

function OngoingCard({ item, i }: { item: { title: string; body: string }; i: number }) {
  const [ref, visible] = useVisible();
  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300"] as const;

  return (
    <div
      ref={ref}
      className={cn(
        "flex gap-4 items-start rounded-[18px] border border-[#E2E8F0] bg-white p-6 transition-all duration-500 ease-out",
        delayClasses[i % delayClasses.length],
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
      )}
    >
      <CheckIcon />
      <div>
        <h3 className="text-[14px] font-semibold text-[#0F172A] mb-1">{item.title}</h3>
        <p className="text-[13.5px] leading-[1.75] text-[#64748B]">{item.body}</p>
      </div>
    </div>
  );
}

function FaqItem({ q, a, i }: { q: string; a: string; i: number }) {
  const [open, setOpen] = useState(false);
  const [ref, visible] = useVisible();
  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300"] as const;

  return (
    <div
      ref={ref}
      className={cn(
        "border-b border-[#E2E8F0] transition-all duration-500 ease-out",
        delayClasses[i % delayClasses.length],
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
      )}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 py-5 text-left"
      >
        <span className="text-[15px] font-semibold text-[#0F172A] leading-[1.5]">{q}</span>
        <div
          className={cn(
            "flex h-7 w-7 items-center justify-center rounded-full transition-colors duration-200",
            open ? "bg-[#1565C0] text-white" : "bg-[#F8FAFC] text-[#1565C0]"
          )}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d={open ? "M2 6h8" : "M6 2v8M2 6h8"}
              stroke={open ? "#FFFFFF" : "#1565C0"}
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </div>
      </button>
      {open && (
        <div className="pb-5">
          <p className="text-[14px] leading-[1.75] text-[#64748B]">{a}</p>
        </div>
      )}
    </div>
  );
}

function ServiceCard({ s, i }: { s: { n: string; title: string; body: string; img: string }; i: number }) {
  const [ref, visible] = useVisible();

  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300"] as const;

  return (
    <div
      ref={ref}
      className={cn(
        "group flex flex-col overflow-hidden rounded-[18px] border border-[#E2E8F0] bg-white shadow-sm transition-all duration-500 ease-out",
        "hover:border-[#CBD5E1] hover:shadow-[0_12px_36px_rgba(0,0,0,0.08)]",
        delayClasses[i % delayClasses.length],
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      )}
    >
      <div className="relative h-[170px] overflow-hidden">
        <Image src={s.img} alt={s.title} fill className="object-cover transition-transform duration-500 ease-out group-hover:scale-105" />
        <div className="absolute top-3 left-3 rounded-sm bg-[#1565C0] px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-white">
          {s.n}
        </div>
      </div>
      <div className="p-5 pb-6">
        <h3 className="text-[14px] font-semibold text-[#0F172A] mb-2 leading-tight">{s.title}</h3>
        <p className="text-[13.5px] leading-[1.75] text-[#64748B]">{s.body}</p>
      </div>
    </div>
  );
}

function Step({ s, i, last }: { s: { n: string; title: string; body: string }; i: number; last: boolean }) {
  const [ref, visible] = useVisible();
  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300"] as const;

  return (
    <div
      ref={ref}
      className={cn(
        "relative flex gap-4 items-start pb-8 last:pb-0 transition-all duration-500 ease-out",
        delayClasses[i % delayClasses.length],
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
      )}
    >
      {!last && <span className="absolute left-5 top-9 bottom-0 w-px bg-[#E2E8F0]" />}
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1565C0] text-[11px] font-bold text-white">
        {s.n}
      </div>
      <div className="pt-1">
        <h3 className="text-[14px] font-semibold text-[#0F172A] mb-1">{s.title}</h3>
        <p className="text-[13.5px] leading-[1.75] text-[#64748B]">{s.body}</p>
      </div>
    </div>
  );
}

const RISKS = [
  { risk: "Regulatory non-compliance penalties", solution: "Proactive compliance monitoring and timely filings" },
  { risk: "Contract disputes and legal exposure", solution: "Professional contract drafting, review and vetting" },
  { risk: "GST and tax liability issues", solution: "Strategic tax planning and accurate GST compliance" },
  { risk: "Labour law violations", solution: "Complete HR and labour law compliance framework" },
  { risk: "Corporate governance failures", solution: "Board governance, statutory meetings and annual filings" },
  { risk: "Government licensing delays", solution: "Expert liaison with regulatory authorities and departments" },
];

const SERVICES = [
  {
    n: "01",
    title: "Business Registration & Statutory Compliance",
    body: "End-to-end assistance in company incorporation (Pvt Ltd, LLP, OPC), GST registration, MSME certification, professional tax, import-export code (IEC) and all statutory registrations required under Indian law.",
    img: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&q=80",
  },
  {
    n: "02",
    title: "Labour & Employment Law Advisory",
    body: "Comprehensive compliance with labour regulations including Shops & Establishments Act, EPF, ESIC, Gratuity Act, Maternity Benefit Act, Industrial Disputes Act and occupational safety standards.",
    img: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=600&q=80",
  },
  {
    n: "03",
    title: "Contract Management & Legal Vetting",
    body: "Professional drafting, review and vetting of commercial agreements, vendor contracts, employment agreements, NDAs, partnership deeds, shareholder agreements and SLAs.",
    img: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=600&q=80",
  },
  {
    n: "04",
    title: "Corporate Governance & Secretarial Services",
    body: "Compliance with Companies Act provisions, board governance, statutory meetings (Board, AGM, EGM), annual MCA filings (AOC-4, MGT-7), maintenance of statutory registers and director KYC.",
    img: "https://images.unsplash.com/photo-1560472355-536de3962603?w=600&q=80",
  },
  {
    n: "05",
    title: "Tax Planning & Regulatory Advisory",
    body: "Strategic tax structuring, advance tax planning, GST compliance (monthly/quarterly GSTR filings), income tax advisory, TDS compliance, transfer pricing and sector-specific regulatory guidance.",
    img: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&q=80",
  },
  {
    n: "06",
    title: "Licensing & Government Liaison",
    body: "Facilitation of business licences, trade licences, regulatory approvals, environmental clearances, fire NOCs, factory licences, RERA registration and liaison with government departments and statutory authorities.",
    img: "https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=600&q=80",
  },
];

const STEPS = [
  { n: "01", title: "Initial Legal Audit & Assessment", body: "Comprehensive evaluation of your business structure, operations and existing compliance status to identify all regulatory gaps and risk areas." },
  { n: "02", title: "Tailored Compliance Strategy", body: "Development of a customised legal and regulatory framework aligned with your specific industry, business model and long-term growth objectives." },
  { n: "03", title: "Implementation & Monitoring", body: "Systematic implementation of compliance protocols with continuous monitoring to ensure adherence to evolving regulatory requirements throughout the year." },
  { n: "04", title: "Expert Legal Consultation", body: "Ongoing access to experienced legal professionals who provide strategic guidance on complex regulatory and compliance matters as they arise." },
  { n: "05", title: "Documentation & Regulatory Filing", body: "Professional management of statutory documentation, registrations, licences and all periodic regulatory filings to maintain full legal compliance." },
];

const ONGOING = [
  { title: "Regulatory Intelligence", body: "Timely updates on amendments in central and state legislation affecting your business" },
  { title: "Audit Support", body: "Professional assistance with statutory audits, departmental inspections and compliance documentation" },
  { title: "MSME & Startup Advisory", body: "Ongoing legal consultation for evolving compliance requirements of growing businesses" },
  { title: "Tender Readiness", body: "End-to-end support for government tender participation and bid compliance" },
  { title: "Dispute Resolution", body: "Expert representation before regulatory authorities, tribunals and administrative bodies" },
  { title: "Integrated Compliance Calendar", body: "Monthly compliance checklist with due dates for all regulatory obligations" },
];

const FAQS = [
  { q: "Why is legal compliance important for my business?", a: "Legal compliance protects your business from penalties, lawsuits and operational disruptions. It also builds trust with customers, investors and government bodies — and is often a prerequisite for funding and tendering." },
  { q: "What legal structure is best for my business?", a: "It depends on your size, industry and goals. Sole Proprietorship is simplest, Pvt Ltd offers liability protection and investor-readiness, LLP combines flexibility with protection. We advise on the right structure during consultation." },
  { q: "How do I protect my intellectual property?", a: "Register trademarks for brand names and logos, file patents for inventions and register copyrights for creative works. We handle the entire process from filing to grant and monitoring." },
  { q: "Is GST compliance mandatory for all businesses?", a: "Yes, if your annual turnover exceeds the threshold (₹40 lakh for goods, ₹20 lakh for services). Voluntary registration is also beneficial for input tax credit and business credibility." },
  { q: "What is a statutory audit and who needs one?", a: "A statutory audit is a mandatory annual audit required under the Companies Act for all registered companies. It ensures financial statements are accurate, complete and compliant with accounting standards." },
  { q: "How quickly can you incorporate a company?", a: "A Private Limited Company can typically be incorporated within 7–10 working days from the date of complete document submission and DSC issuance. We manage the entire process end-to-end." },
];

export default function LegalPage() {
  return (
    <main className="bg-white">
      <section className="grid lg:grid-cols-[1.05fr_0.95fr] min-h-0 lg:min-h-[580px]">
        <div className="flex flex-col justify-center bg-white py-[30px] pl-4 pr-4 sm:pl-8 sm:pr-[30px] lg:pl-[80px]">
          <div className="flex flex-wrap items-center gap-2 text-[12px] leading-6 text-[#64748B] mb-6">
            <Link href="/services" className="font-medium text-[#1565C0] hover:text-[#0D47A1]">
              Services
            </Link>
            <span className="text-[#E2E8F0]">›</span>
            <span className="text-[#0F172A]">Legal Assistance</span>
          </div>

          <div className="flex flex-wrap gap-2.5 mb-5">
            {[
              "Registration",
              "Compliance",
              "Contracts",
              "Tax Advisory",
              "Licensing",
            ].map((tag) => (
              <span key={tag} className="rounded-full bg-[#EFF6FF] px-3 py-1 text-[11px] font-semibold text-[#1565C0]">
                {tag}
              </span>
            ))}
          </div>

          <Eyebrow>Legal &amp; Compliance Solutions</Eyebrow>
          <Divider />
          <h1 className="text-[clamp(26px,3.5vw,44px)] font-extrabold text-[#0F172A] leading-[1.1] mb-6 max-w-[30rem]">
            Comprehensive Legal Protection for Indian Businesses
          </h1>
          <p className="text-[16px] leading-[1.85] text-[#64748B] mb-9 ">
            In India&apos;s complex regulatory landscape, professional legal compliance is not just an administrative requirement — it is a strategic business investment. Farsight safeguards your interests and ensures complete statutory adherence.
          </p>
          <div className="flex flex-wrap gap-4">
            <button className="rounded-sm bg-[#1565C0] px-7 py-3 text-[13px] font-bold uppercase tracking-[0.06em] text-white transition-colors duration-200 hover:bg-[#12579f]">
              BOOK LEGAL AUDIT
            </button>
            <button className="rounded-sm border border-[#E2E8F0] bg-white px-7 py-3 text-[13px] font-semibold uppercase tracking-[0.06em] text-[#0F172A] transition-colors duration-200 hover:bg-[#F8FAFC]">
              TALK TO LEGAL EXPERT
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden w-full min-h-[250px] sm:min-h-[340px] lg:min-h-[420px]">
          <Image
            src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=900&q=85"
            alt="Legal assistance"
            fill
            className="object-cover"
          />
        </div>
      </section>

      <div className="bg-[#1565C0] grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        {[
          { v: "6", l: "Practice Areas" },
          { v: "100%", l: "Statutory Managed" },
          { v: "Free", l: "Legal Audit" },
          { v: "7–10", l: "Days Incorporation" },
        ].map((s, i) => (
          <div key={i} className={cn("py-5 text-center", i < 3 ? "xl:border-r border-white/15" : "")}>
            <div className="text-[24px] font-black text-white leading-none">{s.v}</div>
            <div className="text-[11px] uppercase tracking-[0.08em] text-white/70 mt-1">{s.l}</div>
          </div>
        ))}
      </div>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-2 lg:items-center">
          <Reveal from="left">
            <div>
              <Eyebrow>Why Legal Compliance Matters</Eyebrow>
              <Divider />
              <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-[#0F172A] leading-[1.18] mb-6">
                Protect Your Business from Every Angle
              </h2>
              <p className="mb-9 text-[14px] leading-[1.85] text-[#64748B]">
                Non-compliance in India&apos;s regulatory environment can lead to penalties, licence cancellations and reputational damage. Our proactive approach ensures your business is protected at every stage.
              </p>
              <div className="border-t-2 border-[#1565C0] pt-5">
                {RISKS.map((r, i) => (
                  <RiskRow key={i} r={r} i={i} />
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <div className="relative">
              <div className="relative w-full h-[260px] sm:h-[360px] lg:h-[460px] overflow-hidden rounded-[18px] border border-[#E2E8F0]">
                <Image
                  src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=700&q=80"
                  alt="Legal compliance"
                  fill
                  className="object-cover"
                />
              </div>
              <div className="hidden lg:block absolute -bottom-6 left-0 w-full max-w-[340px] rounded-[18px] bg-white border border-[#E2E8F0] p-6 shadow-[0_8px_32px_rgba(0,0,0,0.10)]">
                <div className="text-[30px] font-black text-[#1565C0] leading-none">100%</div>
                <div className="mt-2 text-[11px] font-medium uppercase tracking-[0.06em] text-[#64748B]">Statutory Managed</div>
                <div className="mt-4 text-[28px] font-black text-[#E65100] leading-none">7–10</div>
                <div className="mt-2 text-[11px] font-medium uppercase tracking-[0.06em] text-[#64748B]">Days to Incorporate</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-[#F8FAFC]">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="mb-10 lg:mb-14">
              <Eyebrow>Our Legal Services Portfolio</Eyebrow>
              <Divider />
              <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-[#0F172A] leading-[1.18] max-w-[640px]">
                  Six Practice Areas. Complete Coverage.
                </h2>
                <p className="max-w-[360px] text-[14px] leading-[1.85] text-[#64748B]">
                  From incorporation to ongoing governance — every legal need of your business, managed by specialists.
                </p>
              </div>
            </div>
          </Reveal>
          <div className="grid gap-6 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <ServiceCard key={s.n} s={s} i={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-2 lg:items-start">
          <Reveal from="left">
            <div>
              <Eyebrow>Our Process</Eyebrow>
              <Divider />
              <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-[#0F172A] leading-[1.18] mb-5">
                5-Step Compliance Path
              </h2>
              <p className="mb-10 text-[14px] leading-[1.85] text-[#64748B]">
                A structured end-to-end approach — from identifying your compliance gaps to managing all ongoing filings, licences and regulatory obligations.
              </p>
              <div className="relative w-full h-[160px] sm:h-[200px] lg:h-[260px] overflow-hidden rounded-[18px] border border-[#E2E8F0]">
                <Image
                  src="https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=700&q=80"
                  alt="Compliance process"
                  fill
                  className="object-cover"
                />
              </div>
            </div>
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="space-y-4">
              {STEPS.map((s, index) => (
                <Step key={s.n} s={s} i={index} last={index === STEPS.length - 1} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-[#F8FAFC]">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="mb-10 lg:mb-14">
              <Eyebrow>Continuous Compliance Support</Eyebrow>
              <Divider />
              <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-[#0F172A] leading-[1.18] max-w-[640px]">
                  Ongoing Legal Partnership
                </h2>
                <p className="max-w-[360px] text-[14px] leading-[1.85] text-[#64748B]">
                  Legal compliance is not a one-time task — it is a continuous obligation. We are your long-term legal partner.
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid gap-2 md:grid-cols-2">
            {ONGOING.map((item, i) => (
              <OngoingCard key={item.title} item={item} i={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-2 lg:items-start">
          <Reveal from="left">
            <div className="relative w-full h-[180px] sm:h-[240px] lg:h-[280px] overflow-hidden rounded-[18px] border border-[#E2E8F0]">
              <Image
                src="https://images.unsplash.com/photo-1560472355-536de3962603?w=700&q=80"
                alt="Legal FAQ"
                fill
                className="object-cover"
              />
            </div>
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div>
              <Eyebrow>Frequently Asked Questions</Eyebrow>
              <Divider />
              <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-[#0F172A] leading-[1.18] mb-6">
                Common Legal Questions
              </h2>
              <p className="mb-9 text-[14px] leading-[1.85] text-[#64748B]">
                Plain answers to the most common legal and compliance questions from Indian business owners.
              </p>
              <div className="border-t border-[#E2E8F0] pt-5">
                {FAQS.map((faq, index) => (
                  <FaqItem key={faq.q} q={faq.q} a={faq.a} i={index} />
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-[#F8FAFC]">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-2 lg:items-center">
          <Reveal from="left">
            <div className="relative w-full h-[220px] sm:h-[300px] lg:h-[380px] overflow-hidden rounded-[18px] border border-[#E2E8F0]">
              <Image
                src="https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=800&q=80"
                alt="Book legal audit"
                fill
                className="object-cover"
              />
            </div>
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div>
              <Eyebrow>Protect Your Business. Stay Compliant. Grow Confidently.</Eyebrow>
              <Divider />
              <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-[#0F172A] leading-[1.18] mb-6">
                Book a Free Legal Audit
              </h2>
              <p className="mb-3 text-[14px] leading-[1.85] text-[#64748B]">
                We identify your compliance gaps and create a clear action plan — no legal jargon, just practical guidance.
              </p>
              <p className="mb-9 text-[14px] leading-[1.85] text-[#64748B]">
                Our legal experts assess your entire compliance status across registration, tax, labour law and governance in the first session.
              </p>
              <div className="flex flex-wrap gap-4">
                <button className="rounded-sm bg-[#1565C0] px-8 py-3 text-[13px] font-bold uppercase tracking-[0.06em] text-white transition-colors duration-200 hover:bg-[#12579f]">
                  BOOK LEGAL AUDIT
                </button>
                <button className="rounded-sm border border-[#E2E8F0] bg-white px-8 py-3 text-[13px] font-semibold uppercase tracking-[0.06em] text-[#0F172A] transition-colors duration-200 hover:bg-[#F8FAFC]">
                  TALK TO LEGAL EXPERT
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
